import { formatUserFullname } from '@/helpers';
import { CustomPermission } from '@/models/custom-permission.model';
import { CustomResponse } from '@/models/custom-response.model';
import { CustomRole } from '@/models/custom-role.model';
import { CustomUser } from '@/models/custom-user.model';
import { PermissionService, AuthenticationService } from '@/services';
import { UserService } from '@/services';
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { PERMISSIONS } from '@/constants';

const Notification = namespace('notification');
const Authentication = namespace('authentication');

@Component
export default class UserEditView extends Vue {
    @Authentication.Getter('hasPermission') private hasPermission: (permission: string) => boolean;
    @Notification.Action('addSuccess') private addSuccess: any;
    @Notification.Action('addError') private addError: any;

    public inFlight: boolean = false;
    public permissions: CustomPermission[] = [];
    public roles: CustomRole[] = [];
    public confirmDelete: boolean = false;

    get fullName(): string {
        return formatUserFullname(this.editUser);
    }

    get groupedPermissions() {
        if (this.permissions && this.permissions.length) {
            const groupedPermissions: any = {};
            this.permissions.forEach((permission: CustomPermission) => {
                const key = permission.label.split('.')[0];
                if (groupedPermissions.hasOwnProperty(key)) {
                    groupedPermissions[key].push(permission);
                } else {
                    groupedPermissions[key] = [permission];
                }
            });
            return groupedPermissions;
        } else {
            return {};
        }
    }

    public editUser: CustomUser = new CustomUser({
        id: -1,
        first_name: '',
        last_name: '',
        email: '',
        permissions: [],
        role: null,
    });

    get canReadRoles(): boolean {
        return this.hasPermission(PERMISSIONS.Role.READ);
    }

    get canReadPermissions(): boolean {
        return false;
        // return this.hasPermission(PERMISSIONS.Permission.READ);
    }

    public mounted(): void {
        const id = parseInt(this.$route.params.id, 10);
        this.getData(id);

        if (this.canReadRoles) {
            this.getRoles();
        }

        if (this.canReadPermissions) {
            this.getPermissions();
        }
    }

    private async getData(id) {
        this.inFlight = true;
        try {
            const response = await UserService.get(id);
            this.editUser = response.data;
            this.inFlight = false;
        } catch (error) {
            this.addError(error.localized, error);
            this.inFlight = false;
        }
    }

    private async getRoles() {
        this.inFlight = true;
        try {
            const response = await PermissionService.listRoles();
            this.roles = response.data;
            this.inFlight = false;
        } catch (error) {
            this.addError(error.localized, error);
            this.inFlight = false;
        }
    }

    private async getPermissions() {
        this.inFlight = true;
        try {
            const response = await PermissionService.listPermissions();
            this.permissions = response.data;
            this.inFlight = false;
        } catch (error) {
            this.addError(error.localized, error);
            this.inFlight = false;
        }
    }

    public handleSubmit(): void {
        if (
            this.editUser.id &&
            this.editUser.first_name &&
            this.editUser.last_name &&
            this.editUser.email &&
            this.editUser.role
        ) {
            UserService.update(this.editUser).then(
                (response: CustomResponse<CustomUser>) => {
                    this.editUser = response.data;
                    this.addSuccess(response.localized);
                },
                (error: CustomResponse<any>) => {
                    this.addError(error.localized, error);
                },
            );
        }
    }

    public forgotPassword(email) {
        AuthenticationService.forgotPassword(email).then(
            (response: CustomResponse<any>) => {
                this.addSuccess(response.localized);
            },
            (error: CustomResponse<any>) => {
                this.addError(error.localized, error);
            },
        );
    }

    public resendConfirmation(email) {
        AuthenticationService.resendConfirmation(email).then(
            (response: CustomResponse<any>) => {
                this.addSuccess(response.localized);
            },
            (error: CustomResponse<any>) => {
                this.addError(error.localized, error);
            },
        );
    }

    public remove(id: number): void {
        UserService.remove(id).then(
            (response: CustomResponse<any>) => {
                this.addSuccess(response.localized);
                this.$router.push({ name: 'userList' });
            },
            (error: CustomResponse<any>) => {
                this.addError(error.localized, error);
            },
        );
    }

    public isRoleSelected(roleId: number): boolean {
        if (this.editUser && this.editUser.role && this.editUser.role.id) {
            return roleId === this.editUser.role.id;
        }
        return false;
    }

    public togglePermissionsForRole(id: number): void {
        if (this.editUser) {
            const searchedRole = this.roles.find((role: CustomRole) => role.id === Number(id));
            this.editUser.permissions = searchedRole.permissions;
            this.editUser.role = searchedRole;
        }
    }

    public isPermissionGroupChecked(label: string): boolean {
        if (this.editUser) {
            const filteredUserPermissions = this.editUser.permissions.filter((permission: CustomPermission) => {
                return permission.label.indexOf(label) === 0;
            });
            const filteredPermissions = this.permissions.filter((permission: CustomPermission) => {
                return permission.label.indexOf(label) === 0;
            });

            return filteredPermissions.length === filteredUserPermissions.length;
        } else {
            return false;
        }
    }

    public toggleAll(checked: boolean, label: string): void {
        if (this.editUser) {
            if (checked) {
                this.addAllPermissionsForLabel(label);
            } else {
                this.removeAllPermissionsForLabel(label);
            }
        }
    }

    public addAllPermissionsForLabel(label: string): void {
        const filteredPermissions = this.permissions.filter((permission: CustomPermission) => {
            return permission.label.indexOf(label) === 0;
        });

        filteredPermissions.forEach((filteredPermission: CustomPermission) => {
            if (this.editUser.permissions.indexOf(filteredPermission) === -1) {
                this.editUser.permissions.push(filteredPermission);
            }
        });
    }

    public removeAllPermissionsForLabel(label: string): void {
        const filteredPermissionIds = this.permissions.filter((permission: CustomPermission) => {
            return permission.label.indexOf(label) === 0;
        }).map((permission: CustomPermission) => permission.id);

        this.editUser.permissions = this.editUser.permissions.filter((permission: CustomPermission) => {
            return filteredPermissionIds.indexOf(permission.id) < 0;
        });
    }

}
